import { useState, type CSSProperties } from "react";
import { api } from "../api/client";
import type { AccountListCreateResult } from "../api/types";
import { EmptyState } from "../components/EmptyState";
import { Icon } from "../components/Icon";
import { ImportWizard } from "../components/ImportWizard";
import { LoadingRows, ResourceError } from "../components/ResourceNotice";
import { StatusPill } from "../components/StatusPill";
import { TaskWizard } from "../components/TaskWizard";
import { useRemoteResource } from "../hooks/useRemoteResource";
import { formatDate } from "../utils/format";

export function AccountsPage() {
  const resource = useRemoteResource(api.accountLists.list);
  const [importOpen, setImportOpen] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  const [initialListId, setInitialListId] = useState<number | null>(null);
  const lists = resource.data?.items || [];
  const totalAccounts = lists.reduce((sum, list) => sum + Number(list.account_count || 0), 0);

  function openTask(listId?: number) {
    setInitialListId(listId || lists[0]?.id || null);
    setTaskOpen(true);
  }

  function onImported(result: AccountListCreateResult, startTask: boolean) {
    resource.reload();
    if (startTask) openTask(result.list.id);
  }

  return (
    <div className="page-stack">
      <section className="page-intro page-intro--accounts">
        <div><span className="eyebrow">服务端持久化</span><h2>从校验到执行，一次完成</h2><p>JSONL 会先逐行预检，再保存为独立账号批次；后续任务进度和失败诊断跟随账号保留。</p></div>
        <div className="page-intro__actions"><button className="button button--quiet" onClick={() => openTask()} disabled={!lists.length}><Icon name="tasks" size={18} />创建任务</button><button className="button button--primary" onClick={() => setImportOpen(true)}><Icon name="accounts" size={18} />导入账号</button></div>
      </section>

      <section className="account-index">
        <div className="account-index__summary">
          <span className="eyebrow">INDEX / ACCOUNT LISTS</span>
          <b>{resource.unsupported ? "—" : totalAccounts}</b>
          <p>{resource.unsupported ? "当前后端尚未开放服务端账号库。" : `分布在 ${lists.length} 个账号列表中`}</p>
          <div className="account-index__legend"><span><i className="dot dot--success" />成功</span><span><i className="dot dot--warning" />运行中</span><span><i className="dot dot--danger" />失败</span></div>
        </div>
        <div className="account-index__content">
          <div className="section-heading"><div><span className="eyebrow">全部列表</span><h2>选择工作集</h2></div><button className="icon-button" onClick={resource.reload} aria-label="刷新账号列表"><Icon name="refresh" size={19} /></button></div>
          {resource.loading ? <LoadingRows count={4} /> : null}
          {resource.error ? <ResourceError message={resource.error} onRetry={resource.reload} /> : null}
          {resource.unsupported ? <EmptyState icon="accounts" title="此环境仍使用浏览器本地账号" description="新前端不会伪造服务端列表。升级账号库 API 后，这里会自动显示真实数据。" /> : null}
          {!resource.loading && !resource.error && !resource.unsupported && !lists.length ? <EmptyState icon="accounts" title="还没有账号批次" description="从 JSONL 文件或粘贴内容开始，服务端校验通过后才会保存。" action={<button className="button button--primary" onClick={() => setImportOpen(true)}>导入第一批账号</button>} /> : null}
          {lists.length ? (
            <div className="account-list-grid">
              {lists.map((list, index) => {
                const count = Number(list.account_count || 0);
                const success = Number(list.success_count || 0);
                const rate = count ? Math.round((success / count) * 100) : 0;
                return (
                  <article className="account-list-row" key={list.id}>
                    <span className="account-list-row__index">{String(index + 1).padStart(2, "0")}</span>
                    <div className="account-list-row__name"><StatusPill status={list.status} /><h3>{list.name}</h3><small>更新于 {formatDate(list.updated_at)}</small></div>
                    <div className="account-list-row__counts"><span><b>{count}</b>账号</span><span><b>{list.running_count || 0}</b>运行中</span><span><b>{list.failed_count || 0}</b>失败</span></div>
                    <div className="account-list-row__rate"><span style={{ "--rate": `${rate}%` } as CSSProperties}><i /></span><b>{rate}%</b><small>成功率</small></div>
                    <button className="icon-button" onClick={() => openTask(list.id)} aria-label={`使用 ${list.name} 创建任务`} title="使用此批次创建任务"><Icon name="arrow" size={19} /></button>
                  </article>
                );
              })}
            </div>
          ) : null}
        </div>
      </section>
      <div className="capability-note"><Icon name="alert" size={19} /><span><b>数据边界</b> 新版只处理现在新导入的账号批次，不读取旧 IndexedDB，也不会自动迁移历史账号。</span></div>
      <ImportWizard open={importOpen} onClose={() => setImportOpen(false)} onCreated={onImported} />
      <TaskWizard open={taskOpen} initialListId={initialListId} onClose={() => setTaskOpen(false)} />
    </div>
  );
}
